import React, { useContext } from 'react';
import { TableColumn } from 'react-data-table-component';
import DefaultDataTable from '../../../../components/default/DefaultDataTable';
import QuoteChangeStatus from '../QuoteChangeStatus';
import { QuoteStatus } from '../../../../app/models/QuoteStatus';
import { momentParseDate } from '../../../../helpers';
import { Quote, QuoteDataForm, QuoteDataFormFilters, QuoteStatusRequestDataForm } from '../../../../app/models/Quote';
import { Client } from '../../../../app/models/Client';
import { Currency } from '../../../../app/models/Currency';
import { Society } from '../../../../app/models/Society';
import { formatMoney } from '../../../../utils/utils';
import { Roles, SectorBusinessRulesEnum } from '../../../../app/shared/enums';
import { AuthContext } from '../../../../contexts/AuthContext';
import ButtonTableAction from '../../../../components/buttons/ButtonTableAction';

interface Props {
    quotes: Quote[];
    statuses: QuoteStatus[];
    callbackSuccess?: () => void;
    totalRows: number;
    loading?: boolean;
    edit?: (quoteId: number) => void;
    show?: (quoteId: number) => void;
    destroy?: (quoteId: number) => void;
    repeat?: (quoteId: number) => void;
    documentQuote?: (quoteId: number, sectorBusinessRulesEnum?: string) => void;

    // Props opcionales añadidas para compatibilidad con definición previa
    availableStatuses?: QuoteStatusRequestDataForm[];
    selectedStatus?: QuoteStatusRequestDataForm;


    filter: QuoteDataFormFilters;
    setFilter: (filter: QuoteDataFormFilters) => void;
    paginationRowsPerPageOptions?: number[];
}

type QuoteStatusOption = QuoteStatus | QuoteStatusRequestDataForm;

type QuoteTableRow = Quote & QuoteDataForm & {
    id: number;
    client?: Client | { name?: string | null } | null;
    client_name?: string | null;
    service_name?: string | null;
    society?: Society | { name?: string | null } | null;
    society_name?: string | null;
    total?: number | string | null;
    currency?: Currency | { name?: string | null } | string | null;
    currency_name?: string | null;
    expired_at?: string | null;
    created_at?: string | null;
    status?: number | string | null;
    status_choices?: QuoteStatusOption[] | null;
    status_selected?: QuoteStatusOption | null;
    availableStatuses?: QuoteStatusRequestDataForm[];
    selectedStatus?: QuoteStatusRequestDataForm;
    sector_business_rules_enum?: SectorBusinessRulesEnum | string | null;
};

const getClientName = (row: QuoteTableRow): string => {
    return row.client?.name?.toString() ?? row.client_name ?? '-';
};

const getSocietyName = (row: QuoteTableRow): string => {
    return row.society?.name?.toString() ?? row.society_name ?? '-';
};

const parseNumber = (value: unknown): number => {
    if (value === null || value === undefined || value === '') {
        return 0;
    }

    const numericValue = typeof value === 'string' ? parseFloat(value) : Number(value);

    return Number.isNaN(numericValue) ? 0 : numericValue;
};

const getCurrencyName = (
    currency: Currency | { name?: string | null } | string | null | undefined
): string => {
    if (!currency) {
        return 'CLP';
    }

    if (typeof currency === 'string') {
        return currency;
    }

    return currency.name?.toString() ?? 'CLP';
};

const getStatusOptions = (
    rowOptions: QuoteTableRow['status_choices'],
    fallback: QuoteStatusOption[]
): QuoteStatusRequestDataForm[] => {
    const source = rowOptions && Array.isArray(rowOptions) && rowOptions.length > 0 ? rowOptions : fallback;

    return source.map((option) => ({ id: option.id, name: option.name }));
};

const getSelectedStatus = (
    statusId: QuoteTableRow['status'],
    rowSelected: QuoteTableRow['status_selected'],
    available: QuoteStatusRequestDataForm[]
): QuoteStatusRequestDataForm | undefined => {
    if (rowSelected) {
        return { id: rowSelected.id, name: rowSelected.name };
    }

    if (statusId !== undefined && statusId !== null) {
        const normalizedId = statusId.toString();
        return available.find((option) => option.id?.toString() === normalizedId);
    }

    return available[0];
};

const QuoteDataTable = ({
    quotes,
    totalRows,
    statuses,
    callbackSuccess,
    loading = false,
    edit,
    destroy,
    show,
    repeat,
    documentQuote,
    filter,
    setFilter
}: Props) => {
    const { auth } = useContext(AuthContext);

    const columns: TableColumn<QuoteTableRow>[] = [
        {
            name: 'Cotización',
            selector: (row: QuoteTableRow) => row.id ?? 0,
            sortable: true,
            sortField: 'id',
            width: '120px',
            cell: (row: QuoteTableRow) => <span className="">{row.id}</span>
        },
        /*{
            name: 'Sector',
            selector: (row: any) => row.orderType,
            sortable: true,
            cell: (row: any) => (
                //<span className={row.orderType === 'Transporte' ? 'badge bg-primary' : 'badge bg-warning'}>
                <span>    
                    {row.orderType}
                </span>
            )
        },*/
        {
            name: 'Sociedad',
            selector: (row: QuoteTableRow) => getSocietyName(row),
            sortable: true,
            sortField: 'society_name',
            cell: (row: QuoteTableRow) => <span>{getSocietyName(row)}</span>
        },
        {
            name: 'Cliente',
            selector: (row: QuoteTableRow) => getClientName(row),
            sortable: true,
            sortField: 'client_name',
            cell: (row: QuoteTableRow) => <span>{getClientName(row)}</span>
        },
        {
            name: 'Sector',
            selector: (row: QuoteTableRow) => {
                if (typeof row.sector === 'string') return row.sector;
                if (Array.isArray(row.sector) && row.sector.length > 0) return (row.sector[0] as any)?.name ?? '';
                return (row.sector as any)?.name ?? '';
            },
            sortable: true,
            sortField: 'sector',
            cell: (row: QuoteTableRow) => {
                const name = typeof row.sector === 'string'
                    ? row.sector
                    : Array.isArray(row.sector)
                        ? (row.sector[0] as any)?.name
                        : (row.sector as any)?.name;
                return <span>{name ?? '-'}</span>;
            }
        },

        {
            name: 'Total',
            selector: (row: QuoteTableRow) => parseNumber(row.total),
            sortable: false,
            sortField: 'total_rate',
            center: true,
            wrap: false,
            width: '140px',
            cell: (row: QuoteTableRow) => {
                const amount = parseNumber(row.total);
                const currencyName = getCurrencyName(row.currency ?? row.currency_name ?? 'CLP');
                if (!amount) return '';
                return <span style={{ display: 'block', width: '100%', textAlign: 'right', paddingRight: '8px' }}>{formatMoney(amount, currencyName)}</span>;
            }
        },
        {
            name: 'Moneda',
            selector: (row: QuoteTableRow) => getCurrencyName(row.currency ?? row.currency_name ?? 'CLP'),
            sortable: true,
            sortField: 'currency_name',
            width: '120px',
            center: true,
            cell: (row: QuoteTableRow) => (
                <span>{getCurrencyName(row.currency ?? row.currency_name ?? 'CLP')}</span>
            )
        },
        {
            name: 'F. Creación',
            selector: (row: QuoteTableRow) => row.created_at ?? '',
            sortable: true,
            sortField: 'created_at',
            center: true,
            cell: (row: QuoteTableRow) => <span>{momentParseDate(row.created_at ?? '')}</span>
        },
        {
            name: 'F. Expiración',
            selector: (row: QuoteTableRow) => row.expired_at ?? '',
            sortable: true,
            sortField: 'expired_at',
            center: true,
            cell: (row: QuoteTableRow) => <span>{momentParseDate(row.expired_at ?? '')}</span>
        },
        {
            name: 'Estado',
            selector: (row: QuoteTableRow) => row.status ?? '',
            sortable: true,
            sortField: 'status',
            center: true,
            cell: (row: QuoteTableRow) => {
                if (!statuses || statuses.length === 0) {
                    return '';
                }
                const availableStatuses = getStatusOptions(row.status_choices, statuses);
                const selectedStatus = getSelectedStatus(row.status, row.status_selected, availableStatuses);
                if (!selectedStatus) {
                    return '';
                }
                return (
                    <QuoteChangeStatus
                        callbackSuccess={callbackSuccess}
                        statuses={availableStatuses}
                        selectedStatus={selectedStatus}
                        quoteId={row.id}
                    />
                );
            }
        },
        {
            name: 'Acciones',
            selector: (row: QuoteTableRow) => row.id ?? 0,
            sortable: false,
            width: '150px',
            cell: (row: QuoteTableRow) => {
                const lockedDestroy = row.status !== 'PREPARACION';
                return (
                    <div className="">

                        {documentQuote && (
                        <ButtonTableAction
                            callbackFunction={() =>
                            // en lugar de sólo row.id, mandamos también el sector
      documentQuote(
        row.id ?? -1,
        row.sector_business_rules_enum
      )
                            }
                            classIcon={'mdi mdi-download'}
                            colorIcon={'text-success'}
                            errorMessage={'No se puede descargar este registro.'}
                            title={'Descargar'}
                        />
                        )}
                        {repeat &&
                            auth?.roles &&
                            (auth?.roles.includes(Roles.MANAGERS) ||
                                auth?.roles.includes(Roles.OPERATIONS_MANAGER)) && (
                                <ButtonTableAction
                                    callbackFunction={() => repeat(row.id ?? -1)}
                                    classIcon={'mdi mdi-content-copy'}
                                    colorIcon={'text-secondary'}
                                    errorMessage={'No se puede duplicar este registro.'}
                                    title={'Duplicar'}
                                />
                            )}
                        {show && (
                            <ButtonTableAction
                                callbackFunction={() => show(row.id ?? -1)}
                                classIcon={'mdi mdi-eye'}
                                colorIcon={'text-info'}
                                errorMessage={'No se puede mostrar este registro.'}
                                title={'Ver'}
                            />
                        )}
                        {edit && (
                            <ButtonTableAction
                                callbackFunction={() => edit(row.id ?? -1)}
                                classIcon={'mdi mdi-pencil'}
                                colorIcon={'text-warning'}
                                errorMessage={'No se puede editar este registro.'}
                                title={'Editar'}
                            />
                        )}
                        {destroy &&
                            auth?.permissions &&
                            (auth?.hasAllPermissions ||
                                auth.permissions.includes('delete_quote')) && (
                                <ButtonTableAction
                                    callbackFunction={() => destroy(row.id ?? -1)}
                                    locked={lockedDestroy}
                                    classIcon={'mdi mdi-delete'}
                                    colorIcon={'text-danger'}
                                    errorMessage={'No se puede eliminar este registro.'}
                                    title={'Eliminar'}
                                />
                            )}
                    </div>
                );
            }
        }
    ];

    return (
        <div className="row">
            <div className="col-12 mt-4">
                <DefaultDataTable
                    columns={columns}
                    data={quotes}
                    progressPending={loading}
                    paginationTotalRows={totalRows}
                    filter={filter}
                    setFilter={setFilter}
                />
            </div>
        </div>
    );
};

export default QuoteDataTable;
